import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, Loader2 } from "lucide-react";
import { submitForm, FormSubmissionData } from "@/utils/formSubmission";
import { ConsentCheckboxes } from "@/components/forms/ConsentCheckboxes";
import { FLEET_PLANS, formatUsd } from "@/lib/fleetPlans";
import { toast } from "sonner";

const PARTNER_PATHS = ["/become-a-partner"];

const POTS_PATHS = [
  "/pots-replacement-solutions",
  "/pots-replacement",
  "/pots-order-form",
];

const HOME_INTERNET_PATHS = [
  "/home-internet",
  "/internet-outside-city-limits",
];

const ALL_SOLUTIONS_PATHS = [
  "/all-solutions",
  "/all-technology-solutions",
  "/communications-solutions",
  "/software-as-a-service-saas",
  "/artificial-intelligence",
  "/managed-services",
  "/infrastructure-solutions",
  "/security-solutions",
  "/energy-solutions",
  "/hardware-and-logistics",
];

// Enterprise Connectivity: business internet, failover, fixed wireless, IoT, Starlink Business
const BUSINESS_PATHS = [
  "/failover-solutions",
  "/fixed-wireless-solutions",
  "/business-internet-solutions",
  "/internet-of-things-iot",
  "/starlink-business-internet",
  "/starlink-not-available",
  "/business-core",
  "/business-advanced",
  "/business-failover",
  "/business-ultimate",
  "/core-failover",
  "/advanced-failover",
  "/mission-critical",
  "/enterprise-shield",
  "/get-connected",
];

// Field Solutions: homepage + field comm pages
const FIELD_PATHS = [
  "/field-tablets-mobile-internet",
  "/field-communications",
  "/jobsite-connectivity",
  "/push-to-talk",
  "/fleet-asset-tracking",
  "/asset-tracking-solutions",
  "/asset-tracking-intake",
];

const matches = (pathname: string, list: string[]) =>
  list.some((p) => pathname === p || pathname.startsWith(p + "/"));

const deriveLeadSourceTag = (pathname: string, source?: string): string => {
  if (matches(pathname, PARTNER_PATHS)) return "channel-partner-lead";
  if (matches(pathname, POTS_PATHS)) return "pots-web-lead";
  if (matches(pathname, HOME_INTERNET_PATHS)) return "home-internet-lead";
  if (matches(pathname, ALL_SOLUTIONS_PATHS)) return "all-solutions";
  if (matches(pathname, BUSINESS_PATHS)) return "business-lead";
  if (pathname === "/" || matches(pathname, FIELD_PATHS)) return "field-comm-lead";
  // Default fallback
  return "field-comm-lead";
};

// Plan options come from the shared catalog so form labels always match the
// published rates on the pricing section.
const helpOptions = [
  ...FLEET_PLANS.map((plan) => ({
    value: plan.key,
    label: `${plan.name} (${formatUsd(plan.rates.standard)}/vehicle/mo)`,
  })),
  { value: "starlink-business", label: "Starlink Business Internet" },
];

const timelineOptions = [
  { value: "asap", label: "ASAP" },
  { value: "30-days", label: "Within 30 days" },
  { value: "1-3-months", label: "1–3 months" },
  { value: "researching", label: "Just researching" },
];

// Maps legacy preSelectedService values to the current plan keys
const serviceToHelpMap: Record<string, string> = {
  "Failover Internet": "fleet_basic",
  "Fixed Wireless": "fleet_basic",
  "Business Internet": "fleet_basic",
  "Starlink Internet": "starlink-business",
  "Starlink Business Internet": "starlink-business",
  "Home Internet": "fleet_basic",
  "Asset Tracking": "fleet_basic",
  "IoT Connectivity": "fleet_basic",
  "POTS Replacement": "fleet_basic",
  "Other/Need Consult": "fleet_basic",
  "Partner Program": "fleet_basic",
  "Basic Tracking": "fleet_basic",
  "Fleet Basic Tracking": "fleet_basic",
  "OBD Tracking": "fleet_obd",
  "Fleet OBD Tracking": "fleet_obd",
  "OBD with Theft Protect": "fleet_obd_backup",
  "Fleet OBD with Backup Tracking": "fleet_obd_backup",
  "Dual Facing Camera": "fleet_camera",
  "Fleet Camera Tracking": "fleet_camera",
};

interface UniversalLeadFormProps {
  /** Pre-select a help topic based on the page context */
  preSelectedService?: string;
  /** Form source for CRM tracking */
  source?: FormSubmissionData["source"];
  /** Visual variant */
  variant?: "dark" | "light";
  /** Button label override */
  buttonText?: string;
  /** Called on successful submission */
  onSuccess?: () => void;
  /** Additional message prefix to prepend */
  messagePrefix?: string;
  /** Override the auto-derived lead_source_tag (channel-partner-lead, pots-lead, field-comm-lead) */
  leadSourceTag?: string;
}

export const UniversalLeadForm = ({
  preSelectedService,
  source = "contact_form",
  variant = "dark",
  buttonText = "Request Consultation",
  onSuccess,
  messagePrefix,
  leadSourceTag,
}: UniversalLeadFormProps) => {
  const location = useLocation();
  const resolvedTag = leadSourceTag || deriveLeadSourceTag(location.pathname, source);
  const mappedHelp = preSelectedService ? serviceToHelpMap[preSelectedService] || "" : "";

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    businessName: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    helpWith: mappedHelp ? [mappedHelp] : ([] as string[]),
    timeline: "",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  const isDark = variant === "dark";
  const inputClass = isDark
    ? "bg-[#0a1128] border-gray-700 text-white focus:border-primary"
    : "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.helpWith.length === 0) {
      toast.error("Please select at least one plan.");
      return;
    }

    setIsSubmitting(true);

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();

    const helpLabels = formData.helpWith
      .map((v) => helpOptions.find((o) => o.value === v)?.label || v);
    const helpLabel = helpLabels.join(", ") || "(not specified)";
    const timelineLabel = timelineOptions.find((o) => o.value === formData.timeline)?.label || formData.timeline;

    const messageParts = [
      messagePrefix,
      `Interested in plans: ${helpLabel}`,
      `Timeline: ${timelineLabel}`,
      formData.notes ? `Notes: ${formData.notes}` : null,
    ].filter(Boolean).join("\n");

    const street = formData.street.trim();
    const city = formData.city.trim();
    const state = formData.state.trim();
    const zip = formData.zip.trim();
    const addressParts = [street, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean);
    const fullAddress = addressParts.join(", ");

    const submissionData: FormSubmissionData = {
      firstName,
      lastName,
      email: formData.email,
      phone: formData.phone || undefined,
      company: formData.businessName || undefined,
      address: street || fullAddress || undefined,
      city: city || undefined,
      state: state || undefined,
      postalCode: zip || undefined,
      message: messageParts,
      source,
      servicesInterested: helpLabels,
      leadSourceTag: resolvedTag,
    };

    try {
      const result = await submitForm(submissionData);

      if (result.success) {
        toast.success("Thank you! We'll be in touch within 2 business hours.");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          businessName: "",
          street: "",
          city: "",
          state: "",
          zip: "",
          helpWith: mappedHelp ? [mappedHelp] : [],
          timeline: "",
          notes: "",
        });
        setSmsConsent(false);
        onSuccess?.();
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("Something went wrong. Please try again or call (888) 534-MUXS (6897).");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Hidden field captured by GHL for lead routing */}
      <input type="hidden" name="lead_source_tag" value={resolvedTag} readOnly />
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName" className={isDark ? "text-white" : ""}>
            First Name *
          </Label>
          <Input
            id="firstName"
            required
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            placeholder="John"
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName" className={isDark ? "text-white" : ""}>
            Last Name *
          </Label>
          <Input
            id="lastName"
            required
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            placeholder="Smith"
            className={inputClass}
          />
        </div>
      </div>

      {/* Email & Phone */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email" className={isDark ? "text-white" : ""}>
            Email *
          </Label>
          <Input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="john@company.com"
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className={isDark ? "text-white" : ""}>
            Phone *
          </Label>
          <Input
            id="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="(555) 123-4567"
            className={inputClass}
          />
        </div>
      </div>

      {/* Business Name */}
      <div className="space-y-2">
        <Label htmlFor="businessName" className={isDark ? "text-white" : ""}>
          Business Name
        </Label>
        <Input
          id="businessName"
          value={formData.businessName}
          onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
          placeholder="Your Company"
          className={inputClass}
        />
      </div>

      {/* Service Address */}
      <div className="space-y-2">
        <Label htmlFor="street" className={isDark ? "text-white" : ""}>
          Address
        </Label>

        <Input
          id="street"
          value={formData.street}
          onChange={(e) => setFormData({ ...formData, street: e.target.value })}
          placeholder="Street address"
          className={inputClass}
        />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city" className={isDark ? "text-white" : ""}>
            City
          </Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            placeholder="City"
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state" className={isDark ? "text-white" : ""}>
            State
          </Label>
          <Input
            id="state"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            placeholder="State"
            maxLength={2}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="zip" className={isDark ? "text-white" : ""}>
            ZIP
          </Label>
          <Input
            id="zip"
            value={formData.zip}
            onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
            placeholder="ZIP"
            className={inputClass}
          />
        </div>
      </div>
      {/* Interested Plans & Timeline */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Label className={isDark ? "text-white" : ""}>
            Which solutions are you interested in? (Select all that apply) *
          </Label>
          <div className="space-y-2">
            {helpOptions.map((opt) => {
              const checked = formData.helpWith.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => {
                      setFormData({
                        ...formData,
                        helpWith: value
                          ? [...formData.helpWith, opt.value]
                          : formData.helpWith.filter((v) => v !== opt.value),
                      });
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="timeline" className={isDark ? "text-white" : ""}>
            When do you need this?
          </Label>
          <Select
            value={formData.timeline}
            onValueChange={(value) => setFormData({ ...formData, timeline: value })}
          >
            <SelectTrigger id="timeline" className={inputClass}>
              <SelectValue placeholder="Select timeline" />
            </SelectTrigger>
            <SelectContent>
              {timelineOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes" className={isDark ? "text-white" : ""}>
          Additional Notes
        </Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Anything else we should know..."
          rows={3}
          className={inputClass}
        />
      </div>

      {/* Consent */}
      <ConsentCheckboxes
        consent={smsConsent}
        onConsentChange={setSmsConsent}
        variant={isDark ? "dark" : "light"}
      />

      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={isSubmitting || !smsConsent}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            {buttonText} <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        Same-day quotes for most services • US-based support
      </p>
    </form>
  );
};
